import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Sparkles, Loader2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationDetails = ({ user }) => {
  const navigate = useNavigate();
  const { locationName } = useParams();
  const canUseAiInsights = user?.email === "jcarter@mybestvaluerx.com";
  const [searchParams] = useSearchParams();
  const urlYear = searchParams.get('year');
  const [plData, setPlData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'mtd');
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiRef = useRef(null);

  useEffect(() => {
    loadPeriods();
    loadYears();
  }, []);

  useEffect(() => {
    if (viewMode === 'mtd' && selectedPeriod) {
      loadPLData();
    } else if (viewMode === 'ytd' && selectedYear) {
      loadYTDData();
    }
    setAiInsight("");
    setAiError(null);
  }, [selectedPeriod, selectedYear, locationName, viewMode]);

  const loadPeriods = async () => {
    try {
      const cacheBreaker = new Date().getTime();
      const response = await axios.get(`${API}/periods?_=${cacheBreaker}`);
      setPeriods(response.data.periods);
      if (response.data.periods.length > 0) {
        const sortedPeriods = response.data.periods.sort();
        setSelectedPeriod(sortedPeriods[sortedPeriods.length - 1]); // Most recent period
      }
    } catch (error) {
      console.error("Error loading periods:", error);
    }
  };

  const loadYears = async () => {
    try {
      const cacheBreaker = new Date().getTime();
      const response = await axios.get(`${API}/ytd/years?_=${cacheBreaker}`);
      const years = response.data.years || [];
      setAvailableYears(years);
      if (years.length > 0) {
        // Use URL year if provided and valid, otherwise default to most recent
        if (urlYear && years.includes(urlYear)) {
          setSelectedYear(urlYear);
        } else {
          setSelectedYear(years[0]); // First entry is most recent (sorted desc from API)
        }
      }
    } catch (error) {
      console.error("Error loading years:", error);
    }
  };

  const loadYTDData = async () => {
    setLoading(true);
    try {
      const cacheBreaker = new Date().getTime();
      const response = await axios.get(
        `${API}/ytd/location/${encodeURIComponent(locationName)}/${selectedYear}?_=${cacheBreaker}`
      );
      
      setYtdData(response.data);
    } catch (error) {
      console.error("Error loading YTD data:", error);
      setYtdData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPLData = async () => {
    setLoading(true);
    try {
      const cacheBreaker = new Date().getTime();
      const response = await axios.get(`${API}/pl/location/${locationName}?_=${cacheBreaker}`, {
        params: { period: selectedPeriod }
      });
      setPlData(response.data);
    } catch (error) {
      console.error("Error loading P&L data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const generateAiInsight = async () => {
    setAiInsight("");
    setAiError(null);
    setAiLoading(true);

    const token = localStorage.getItem('token');
    
    try {
      const params = new URLSearchParams();
      if (viewMode === 'mtd' && selectedPeriod) {
        params.append('period', selectedPeriod);
      } else if (viewMode === 'ytd' && selectedYear) {
        params.append('year', selectedYear);
      }
      const url = `${API}/insights/location/${encodeURIComponent(locationName)}${params.toString() ? '?' + params : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Failed to generate insight' }));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') break;
            if (data.startsWith('[ERROR]')) {
              throw new Error(data.slice(8));
            }
            fullText += data;
            setAiInsight(fullText);
          }
        }
      }

      // Scroll to the insight card
      setTimeout(() => aiRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-300 border-t-slate-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">{locationName}</h1>
              <p className="text-sm text-slate-600">
                Detailed P&L Breakdown ({viewMode === 'mtd' ? 'MTD' : 'YTD'})
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* MTD/YTD Toggle */}
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'mtd' ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode('mtd')}
              >
                MTD
              </Button>
              <Button
                variant={viewMode === 'ytd' ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode('ytd')}
              >
                YTD
              </Button>
            </div>
            
            {/* Period/Year Selector */}
            {viewMode === 'mtd' && periods.length > 0 && (
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem key={period} value={period}>{period}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {viewMode === 'ytd' && availableYears.length > 0 && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {viewMode === 'mtd' && !plData ? (
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-slate-600">No detailed P&L data available for this location and period.</p>
              <p className="text-sm text-slate-500 mt-2">Upload a QuickBooks P&L report to see detailed breakdowns.</p>
            </CardContent>
          </Card>
        ) : viewMode === 'ytd' && !ytdData ? (
          <Card>
            <CardContent className="py-16 text-center">
              <p className="text-slate-600">No YTD data available for this location.</p>
              <p className="text-sm text-slate-500 mt-2">Upload P&L reports to see year-to-date summaries.</p>
            </CardContent>
          </Card>
        ) : viewMode === 'mtd' && plData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase">Net Income (MTD)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    {plData.net_income >= 0 ? (
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    ) : (
                      <TrendingDown className="h-6 w-6 text-rose-600" />
                    )}
                    <span className={`text-3xl font-bold ${plData.net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(plData.net_income)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase">Gross Profit (MTD)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    {plData.gross_profit >= 0 ? (
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    ) : (
                      <TrendingDown className="h-6 w-6 text-rose-600" />
                    )}
                    <span className={`text-3xl font-bold ${plData.gross_profit >= 0 ? 'text-slate-800' : 'text-rose-600'}`}>{formatCurrency(plData.gross_profit)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Margin: {((plData.gross_profit / plData.total_income) * 100).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase">Total Expenses (MTD)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-slate-800">{formatCurrency(plData.total_expenses)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {plData.expense_items.length} line items
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed P&L */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <CardTitle className="text-slate-800">Profit & Loss Statement</CardTitle>
                <CardDescription>Period: {plData.period}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Income Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Income</h3>
                  {plData.income_items.length === 0 ? (
                    <p className="text-sm text-slate-500 italic ml-4">No income items</p>
                  ) : (
                    <div className="space-y-2 ml-4">
                      {plData.income_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-700">{item.line_item}</span>
                          <span className="font-medium text-slate-800">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded font-semibold">
                        <span className="text-slate-800">Total Income</span>
                        <span className="text-emerald-600">{formatCurrency(plData.total_income)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* COGS Section */}
                {plData.cogs_items.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Cost of Goods Sold</h3>
                    <div className="space-y-2 ml-4">
                      {plData.cogs_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-700">{item.line_item}</span>
                          <span className="font-medium text-slate-800">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded font-semibold">
                        <span className="text-slate-800">Total COGS</span>
                        <span className="text-rose-600">{formatCurrency(plData.total_cogs)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-4 px-4 mt-4 bg-slate-100 rounded-lg font-bold text-lg">
                      <span className="text-slate-800">Gross Profit</span>
                      <span className="text-emerald-600">{formatCurrency(plData.gross_profit)}</span>
                    </div>
                  </div>
                )}

                {/* Expenses Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Expenses</h3>
                  {plData.expense_items.length === 0 ? (
                    <p className="text-sm text-slate-500 italic ml-4">No expense items</p>
                  ) : (
                    <div className="space-y-2 ml-4">
                      {plData.expense_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-700">{item.line_item}</span>
                          <span className="font-medium text-slate-800">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded font-semibold">
                        <span className="text-slate-800">Total Expenses</span>
                        <span className="text-rose-600">{formatCurrency(plData.total_expenses)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Net Income */}
                <div className="flex justify-between items-center py-6 px-6 bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl font-bold text-xl border-2 border-slate-200">
                  <span className="text-slate-800">Net Income</span>
                  <span className={plData.net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                    {formatCurrency(plData.net_income)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights Card */}
            {canUseAiInsights && (
            <Card ref={aiRef} className="border-slate-200 shadow-sm mt-8" data-testid="ai-insights-card">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-slate-50 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-600" />
                    <CardTitle className="text-slate-800">AI Financial Insights</CardTitle>
                  </div>
                  <Button
                    size="sm"
                    onClick={generateAiInsight}
                    disabled={aiLoading}
                    data-testid="generate-insight-btn"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {aiLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        {aiInsight ? 'Regenerate' : 'Generate Insight'}
                      </>
                    )}
                  </Button>
                </div>
                <CardDescription>AI-powered analysis of this location&apos;s financial performance</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {aiError && (
                  <div className="text-rose-600 text-sm bg-rose-50 p-3 rounded-lg mb-4" data-testid="ai-error">
                    {aiError}
                  </div>
                )}
                {aiInsight ? (
                  <div className="text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ai-insight-text">
                    {aiInsight}
                  </div>
                ) : !aiLoading && !aiError ? (
                  <p className="text-slate-500 text-sm italic">
                    Click &quot;Generate Insight&quot; to get an AI-powered analysis of why this location is making or losing money.
                  </p>
                ) : null}
                {aiLoading && !aiInsight && (
                  <div className="flex items-center gap-3 text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Analyzing financial data for {locationName}...</span>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </>
        ) : viewMode === 'ytd' && ytdData ? (
          <>
            {/* YTD Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase">YTD Net Income</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    {ytdData.summary.net_income >= 0 ? (
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    ) : (
                      <TrendingDown className="h-6 w-6 text-rose-600" />
                    )}
                    <span className={`text-3xl font-bold ${ytdData.summary.net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(ytdData.summary.net_income)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase">YTD Gross Profit</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    {ytdData.summary.gross_profit >= 0 ? (
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    ) : (
                      <TrendingDown className="h-6 w-6 text-rose-600" />
                    )}
                    <span className={`text-3xl font-bold ${ytdData.summary.gross_profit >= 0 ? 'text-slate-800' : 'text-rose-600'}`}>{formatCurrency(ytdData.summary.gross_profit)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Margin: {((ytdData.summary.gross_profit / ytdData.summary.total_income) * 100).toFixed(1)}%
                  </p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase">YTD Total Expenses</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-slate-800">{formatCurrency(ytdData.summary.total_expenses)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {ytdData.expense_items.length} line items
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed YTD P&L */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="bg-slate-50 border-b border-slate-200">
                <CardTitle className="text-slate-800">Year-to-Date Profit & Loss Statement</CardTitle>
                <CardDescription>Year: {ytdData.year}</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Income Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Income</h3>
                  {ytdData.income_items.length === 0 ? (
                    <p className="text-sm text-slate-500 italic ml-4">No income items</p>
                  ) : (
                    <div className="space-y-2 ml-4">
                      {ytdData.income_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-700">{item.line_item}</span>
                          <span className="font-medium text-slate-800">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded font-semibold">
                        <span className="text-slate-800">Total Income</span>
                        <span className="text-emerald-600">{formatCurrency(ytdData.summary.total_income)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* COGS Section */}
                {ytdData.cogs_items.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Cost of Goods Sold</h3>
                    <div className="space-y-2 ml-4">
                      {ytdData.cogs_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-700">{item.line_item}</span>
                          <span className="font-medium text-slate-800">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded font-semibold">
                        <span className="text-slate-800">Total COGS</span>
                        <span className="text-rose-600">{formatCurrency(ytdData.summary.total_cogs)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-4 px-4 mt-4 bg-slate-100 rounded-lg font-bold text-lg">
                      <span className="text-slate-800">Gross Profit</span>
                      <span className="text-emerald-600">{formatCurrency(ytdData.summary.gross_profit)}</span>
                    </div>
                  </div>
                )}

                {/* Expenses Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 pb-2 border-b-2 border-slate-200">Expenses</h3>
                  {ytdData.expense_items.length === 0 ? (
                    <p className="text-sm text-slate-500 italic ml-4">No expense items</p>
                  ) : (
                    <div className="space-y-2 ml-4">
                      {ytdData.expense_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-100">
                          <span className="text-slate-700">{item.line_item}</span>
                          <span className="font-medium text-slate-800">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center py-3 bg-slate-50 px-4 rounded font-semibold">
                        <span className="text-slate-800">Total Expenses</span>
                        <span className="text-rose-600">{formatCurrency(ytdData.summary.total_expenses)}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Net Income */}
                <div className="flex justify-between items-center py-6 px-6 bg-gradient-to-r from-slate-100 to-slate-50 rounded-xl font-bold text-xl border-2 border-slate-200">
                  <span className="text-slate-800">Net Income</span>
                  <span className={ytdData.summary.net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                    {formatCurrency(ytdData.summary.net_income)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* AI Insights Card (YTD) */}
            {canUseAiInsights && (
            <Card ref={aiRef} className="border-slate-200 shadow-sm mt-8" data-testid="ai-insights-card">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-slate-50 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-600" />
                    <CardTitle className="text-slate-800">AI Financial Insights</CardTitle>
                  </div>
                  <Button
                    size="sm"
                    onClick={generateAiInsight}
                    disabled={aiLoading}
                    data-testid="generate-insight-btn"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {aiLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        {aiInsight ? 'Regenerate' : 'Generate Insight'}
                      </>
                    )}
                  </Button>
                </div>
                <CardDescription>AI-powered analysis of this location&apos;s financial performance</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {aiError && (
                  <div className="text-rose-600 text-sm bg-rose-50 p-3 rounded-lg mb-4" data-testid="ai-error">
                    {aiError}
                  </div>
                )}
                {aiInsight ? (
                  <div className="text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ai-insight-text">
                    {aiInsight}
                  </div>
                ) : !aiLoading && !aiError ? (
                  <p className="text-slate-500 text-sm italic">
                    Click &quot;Generate Insight&quot; to get an AI-powered analysis of why this location is making or losing money.
                  </p>
                ) : null}
                {aiLoading && !aiInsight && (
                  <div className="flex items-center gap-3 text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Analyzing financial data for {locationName}...</span>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default LocationDetails;
